import React from 'react';
import { Timeline, Title, Text, Skeleton, Badge } from '@mantine/core';
import {
    IconCheck,
    IconPlus,
    IconX,
    IconAlertTriangle,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';

import { Request, PriceChangeRequestHistoryItem } from '../../codegen/graphql';
import { GET_PRICE_CHANGE_REQUEST_HISTORY } from '../../apollo/queries/priceChangeRequest';
import { When } from '../common';
import PriceChangeRequestEscalationEventsDropdown from './history/PriceChangeRequestEscalationEventsDropdown';

type Props = {
    requestId: string | null;
    flatRequest: Request | null;
};

function RequestHistory({ requestId, flatRequest }: Props) {
    const {
        data: historyData,
        loading: loadingHistory,
        error: historyError,
    } = useQuery(GET_PRICE_CHANGE_REQUEST_HISTORY, {
        variables: {
            id: requestId,
        },
        skip: !requestId,
        fetchPolicy: 'network-only',
    });

    if (!requestId) {
        return null;
    }

    const getStatusIcon = (status: string) => {
        if (status === 'Escalated') {
            return <IconAlertTriangle size={12} />;
        }
        if (status.includes('Approved')) {
            return <IconCheck size={12} />;
        }
        if (status.includes('Declined')) {
            return <IconX size={12} />;
        }
        return <IconPlus size={12} />;
    };

    const getStatusColor = (status: string) => {
        if (status === 'Escalated') {
            return 'gray';
        }
        if (status.includes('Approved')) {
            return 'primary';
        }
        if (status.includes('Declined')) {
            return 'red';
        }
        return 'gray';
    };

    const getTimelineItem = ({
        status,
        title,
        actionId,
        description,
        escalationMessage,
        showEscalationBadge: showEscalationBadgeFromApi,
        showPriceChangeFreezeBadge,
        escalationEvents,
        reasonForChange,
        declineReason,
        cashbuildPrice,
        pnlPrice,
        temporaryChangedPrice,
        temporaryCashbuildPrice,
        temporaryPnlPrice,
        temporaryChangeReason,
        note,
        date,
    }: PriceChangeRequestHistoryItem) => {
        const isDivisionManagerEscalatedRow =
            status === 'Escalated' && title.includes('Division Manager');
        const isOpsExecutiveEscalatedRow =
            status === 'Escalated' && title.includes('Ops Executive');
        const isSeniorOpsExecutiveEscalatedRow =
            status === 'Escalated' && title.includes('Senior Ops Executive');
        const isProcurementManagerEscalatedRow =
            status === 'Escalated' && title.includes('Procurement Manager');
        const showEscalationBadge =
            isDivisionManagerEscalatedRow ||
            isOpsExecutiveEscalatedRow ||
            isSeniorOpsExecutiveEscalatedRow ||
            isProcurementManagerEscalatedRow ||
            !!showEscalationBadgeFromApi ||
            (!!escalationMessage &&
                (title.includes('Division Manager') ||
                    title.includes('Ops Executive') ||
                    title.includes('Senior Ops Executive') ||
                    title.includes('Procurement Manager')));
        return (
            <Timeline.Item
                bullet={getStatusIcon(status)}
                title={
                    <>
                        <span>{title}</span>
                        <When condition={showEscalationBadge}>
                            <Badge
                                color="red"
                                size="sm"
                                variant="light"
                                className="shrink-0"
                            >
                                Escalated
                            </Badge>
                        </When>
                        <When condition={!!showPriceChangeFreezeBadge}>
                            <Badge
                                color="cyan"
                                size="sm"
                                variant="light"
                                className="shrink-0"
                            >
                                Price Change Freeze
                            </Badge>
                        </When>
                    </>
                }
                key={actionId}
                color={getStatusColor(status)}
                className={
                    isDivisionManagerEscalatedRow ||
                    isOpsExecutiveEscalatedRow ||
                    isSeniorOpsExecutiveEscalatedRow ||
                    isProcurementManagerEscalatedRow
                        ? 'opacity-60'
                        : undefined
                }
            >
                <div className="flex flex-col gap-[3px]">
                    <Text c="dimmed" size="sm" className="leading-snug">
                        {description}
                    </Text>
                    <When condition={!!escalationMessage}>
                        <Text c="red" size="sm" className="leading-snug">
                            {escalationMessage}
                        </Text>
                    </When>
                    <PriceChangeRequestEscalationEventsDropdown
                        events={escalationEvents}
                    />
                    {status === 'ApprovedWithTemporaryPrice' && (
                        <>
                            {temporaryCashbuildPrice != null && (
                                <Text size="sm" color="rgba(250, 176, 5, 0.5)">
                                    <strong>Interim Cashbuild Price: </strong>
                                    {temporaryCashbuildPrice}
                                </Text>
                            )}
                            {temporaryPnlPrice && (
                                <Text size="sm" color="rgba(250, 176, 5, 0.5)">
                                    <strong>Interim P&L Price: </strong>
                                    {temporaryPnlPrice}
                                </Text>
                            )}
                            {temporaryChangedPrice != null &&
                                temporaryCashbuildPrice == null &&
                                temporaryPnlPrice == null && (
                                    <Text
                                        size="sm"
                                        color="rgba(250, 176, 5, 0.5)"
                                    >
                                        <strong>Interim Price: </strong>
                                        {temporaryChangedPrice}
                                    </Text>
                                )}
                        </>
                    )}
                    {cashbuildPrice && (
                        <Text
                            size="sm"
                            color={
                                status === 'Approved'
                                    ? 'rgba(18, 184, 134, 0.65)'
                                    : 'rgba(250, 176, 5, 0.5)'
                            }
                        >
                            <strong>Final Cashbuild Price: </strong>
                            {cashbuildPrice}
                        </Text>
                    )}
                    {pnlPrice && (
                        <Text
                            size="sm"
                            color={
                                status === 'Approved'
                                    ? 'rgba(18, 184, 134, 0.65)'
                                    : 'rgba(250, 176, 5, 0.5)'
                            }
                        >
                            <strong>Final P&L Price: </strong>
                            {pnlPrice}
                        </Text>
                    )}
                    {declineReason && (
                        <Text color="dimmed" size="sm">
                            <strong>Reason: </strong>
                            {declineReason}
                        </Text>
                    )}
                    {reasonForChange && (
                        <Text color="dimmed" size="sm">
                            <strong>Reason: </strong>
                            {reasonForChange}
                        </Text>
                    )}
                    {temporaryChangeReason && (
                        <Text color="dimmed" size="sm">
                            <strong>Reason: </strong>
                            {temporaryChangeReason}
                        </Text>
                    )}
                    {note && (
                        <Text color="dimmed" size="sm">
                            <strong>Note: </strong>
                            {note}
                        </Text>
                    )}
                    {date && (
                        <Text size="xs" mt={4}>
                            {dayjs(date).fromNow()}
                        </Text>
                    )}
                </div>
            </Timeline.Item>
        );
    };

    const timelineItems =
        historyData?.getPriceChangeRequestHistory?.history ?? [];

    const activeIndex = timelineItems.findLastIndex(
        (item: PriceChangeRequestHistoryItem) =>
            item.status.includes('Approved') ||
            item.status.includes('Declined'),
    );

    return (
        <div className="flex flex-col flex-1 shadow-sm bg-white px-5 py-4 rounded-lg">
            <Title order={4}>Request History</Title>
            {loadingHistory && (
                <div className="flex flex-col gap-5 mt-4">
                    {[...Array(3)].map((_, i) => (
                        <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={`skeleton-${i}`}
                            className="flex gap-3 items-start"
                        >
                            <Skeleton height={24} width={24} circle />
                            <div className="flex flex-col gap-2 flex-1">
                                <Skeleton height={12} width="40%" />
                                <Skeleton height={10} width="70%" />
                                <Skeleton height={8} width={60} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {!loadingHistory && !historyError && (
                <Timeline
                    active={activeIndex >= 0 ? activeIndex : 0}
                    bulletSize={24}
                    lineWidth={2}
                    mt={18}
                    classNames={{
                        itemTitle:
                            'flex flex-wrap items-center gap-x-2 gap-y-1 !leading-snug',
                    }}
                    color={
                        flatRequest?.latestStatus !== 'Declined'
                            ? 'primary'
                            : 'red'
                    }
                >
                    {timelineItems.map(
                        (timelineItem: PriceChangeRequestHistoryItem) =>
                            getTimelineItem(timelineItem),
                    )}
                </Timeline>
            )}
        </div>
    );
}

export default RequestHistory;
